import { useState, useMemo } from "react"
import { Label } from "@/components/ui/label"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { Calendar } from "@/components/ui/calendar"
import { cn } from "@/lib/utils"
import { CalendarIcon } from "lucide-react"
import { format, parse, isValid } from "date-fns"
import { DATE_FORMAT } from "../constants"
import { ChangedPill } from "./display-helpers"
import { useTranslation } from "@/hooks/use-translation"

export interface DateFieldProps {
  label: string
  value: string
  onChange: (value: string) => void
  error?: string
  required?: boolean
  atsHint?: React.ReactNode
  changedPill?: boolean
  disabled?: boolean
}

export function DateField({
  label,
  value,
  onChange,
  error,
  required,
  atsHint,
  changedPill,
  disabled,
}: DateFieldProps) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)

  const parsed = useMemo(() => {
    if (!value) return undefined
    const d = parse(value, DATE_FORMAT, new Date())
    return isValid(d) ? d : undefined
  }, [value])

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center gap-2 flex-wrap">
        <Label className="text-sm font-medium text-gray-700">
          {label}
          {required && <span className="text-red-500 ml-0.5">*</span>}
        </Label>
        {atsHint}
        {changedPill && <ChangedPill />}
      </div>
      <div className="relative">
        <input
          type="text"
          placeholder={t("placeholderDate")}
          value={value}
          onChange={(e) => {
            const v = e.target.value
              .replace(/[^0-9-]/g, "")
              .slice(0, 10)
            onChange(v)
          }}
          readOnly={disabled}
          className={cn(
            "h-9 w-full rounded-md border bg-background px-3 pr-9 text-sm tabular-nums",
            "ring-offset-background placeholder:text-muted-foreground",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
            error ? "border-red-300 focus-visible:ring-red-400" : "border-input",
            disabled && "opacity-50 cursor-not-allowed",
          )}
        />
        <Popover open={disabled ? false : open} onOpenChange={disabled ? undefined : setOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              disabled={disabled}
              className={cn(
                "absolute right-1 top-1 h-7 w-7 inline-flex items-center justify-center rounded text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors focus:outline-none focus-visible:ring-1 focus-visible:ring-ring focus-visible:ring-offset-1",
                disabled && "opacity-50 cursor-not-allowed hover:bg-transparent hover:text-gray-400",
              )}
            >
              <CalendarIcon size={14} />
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="end">
            <Calendar
              mode="single"
              selected={parsed}
              onSelect={(date) => {
                if (date) {
                  onChange(format(date, DATE_FORMAT))
                  setOpen(false)
                }
              }}
              initialFocus
            />
          </PopoverContent>
        </Popover>
      </div>
      <p className="text-xs text-red-500 min-h-4 mt-0.5">{error}</p>
    </div>
  )
}
